import React, { FormEvent, useEffect, useState } from 'react';
import { IClickDataResponse, PanelService } from '../../../network/panel/network';
import './click-data.scss';

const EMPTY_RESPONSE: IClickDataResponse = {
  data: [],
  pagination: { page: 1, limit: 50, total: 0, pages: 1 },
  apps: [],
};

const PERIODS = [
  { value: '1', label: '24 години' },
  { value: '7', label: '7 днів' },
  { value: '30', label: '30 днів' },
  { value: 'all', label: 'Весь час' },
];

function formatDate(value: number): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value));
}

function streamLabel(name: string, key: string): string {
  return String(name || key).replace(/\s*\([^)]*\)\s*$/, '') || key;
}

export default function ClickData() {
  const [result, setResult] = useState<IClickDataResponse>(EMPTY_RESPONSE);
  const [appId, setAppId] = useState('');
  const [period, setPeriod] = useState('7');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const days = period === 'all' ? null : Number(period);
    const from = days ? Date.now() - days * 24 * 60 * 60 * 1000 : undefined;

    PanelService.getClickData({
      appId: appId ? Number(appId) : undefined,
      page,
      limit: 50,
      from,
      search: appliedSearch || undefined,
    })
      .then((response) => {
        if (active) setResult(response);
      })
      .catch(() => {
        if (active) setError('Не вдалося завантажити кліки. Спробуйте оновити сторінку.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [appId, period, appliedSearch, page, refreshKey]);

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  return (
    <main className="click-data-page">
      <section className="click-data-card">
        <header className="click-data-header">
          <div>
            <h1>Кліки по застосунках</h1>
            <p>{result.pagination.total} переходів у потоки</p>
          </div>
          <button type="button" className="click-data-refresh" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>
            Оновити
          </button>
        </header>

        <form className="click-data-filters" onSubmit={applySearch}>
          <label>
            <span>Застосунок</span>
            <select value={appId} onChange={(event) => { setAppId(event.target.value); setPage(1); }}>
              <option value="">Усі застосунки</option>
              {result.apps.map((app) => (
                <option value={app.id} key={app.id}>#{app.id} {app.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Період</span>
            <select value={period} onChange={(event) => { setPeriod(event.target.value); setPage(1); }}>
              {PERIODS.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="click-data-search">
            <span>Пошук</span>
            <div>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="IP, session ID або потік"
              />
              <button type="submit">Знайти</button>
            </div>
          </label>
        </form>

        {error && <div className="click-data-error">{error}</div>}

        <div className="click-data-table-wrap">
          <table className="click-data-table">
            <thead>
              <tr>
                <th>Час</th>
                <th>Застосунок</th>
                <th>GEO</th>
                <th>Потік Keitaro</th>
                <th>Клік</th>
              </tr>
            </thead>
            <tbody>
              {!loading && result.data.length === 0 && (
                <tr><td colSpan={5} className="click-data-empty">Кліків за цими умовами немає</td></tr>
              )}
              {loading && result.data.length === 0 && (
                <tr><td colSpan={5} className="click-data-empty">Завантаження…</td></tr>
              )}
              {result.data.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{formatDate(row.clickedAt)}</strong>
                    <small>{row.platform || 'offerwall'}</small>
                  </td>
                  <td>
                    <strong>{row.app ? `#${row.app.id} ${row.app.name}` : 'Невідомий застосунок'}</strong>
                    <small>{row.app?.bundle || '—'}</small>
                  </td>
                  <td><span className="click-data-geo">{row.country || '—'}</span></td>
                  <td>
                    <strong>{streamLabel(row.stream.name, row.stream.key)}</strong>
                    <small>
                      {[
                        row.stream.id ? `потік #${row.stream.id}` : null,
                        row.stream.key,
                        row.offer?.keitaroId ? `оффер #${row.offer.keitaroId}` : null,
                      ].filter(Boolean).join(' · ')}
                    </small>
                  </td>
                  <td className="click-data-identifiers" title={row.userAgent || undefined}>
                    <strong>{row.ip || 'IP невідомий'}</strong>
                    <small>{row.sessionId}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="click-data-pagination">
          <span>Сторінка {result.pagination.page} з {result.pagination.pages}</span>
          <div>
            <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={loading || page <= 1}>Назад</button>
            <button type="button" onClick={() => setPage((value) => value + 1)} disabled={loading || page >= result.pagination.pages}>Далі</button>
          </div>
        </footer>
      </section>
    </main>
  );
}
